import React from 'react';
import Button from '../../../components/ui/Button';
import LineItemsTable from './LineItemsTable';

const ViewDetailsModal = ({ isOpen, onClose, item, type, loading }) => {
  if (!isOpen) return null;

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: 2
    })?.format(amount || 0);
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    return new Date(date)?.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      // Invoice statuses
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      viewed: 'bg-indigo-100 text-indigo-800',
      paid: 'bg-green-100 text-green-800',
      partially_paid: 'bg-yellow-100 text-yellow-800',
      overdue: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800',

      // Quote statuses
      accepted: 'bg-green-100 text-green-800',
      declined: 'bg-red-100 text-red-800',
      expired: 'bg-orange-100 text-orange-800',
      converted: 'bg-purple-100 text-purple-800',

      // Credit note statuses
      issued: 'bg-blue-100 text-blue-800',
      applied: 'bg-green-100 text-green-800',

      // Customer statuses
      active: 'bg-green-100 text-green-800',
      inactive: 'bg-gray-100 text-gray-800',
      suspended: 'bg-red-100 text-red-800',
      archived: 'bg-orange-100 text-orange-800',

      // Payment statuses
      pending: 'bg-yellow-100 text-yellow-800',
      cleared: 'bg-green-100 text-green-800',
      bounced: 'bg-red-100 text-red-800'
    };

    return statusConfig?.[status] || 'bg-gray-100 text-gray-800';
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      );
    }

    if (!item) {
      return (
        <div className="text-center py-12">
          <p className="text-gray-500">No data available</p>
        </div>
      );
    }

    switch (type) {
      case 'invoices':
        return (
          <div className="space-y-6">
            {/* Invoice Header */}
            <div className="border-b pb-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {item?.invoice_number}
                  </h2>
                  <p className="text-gray-600">Invoice Details</p>
                </div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(item?.status)}`}>
                  {item?.status?.replace('_', ' ')?.toUpperCase()}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Issue Date</dt>
                  <dd className="text-sm text-gray-900">{formatDate(item?.issue_date)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Due Date</dt>
                  <dd className="text-sm text-gray-900">{formatDate(item?.due_date)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Payment Terms</dt>
                  <dd className="text-sm text-gray-900">{item?.payment_terms?.replace('_', ' ')}</dd>
                </div>
              </div>
            </div>
            {/* Customer Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Customer Information</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Customer Name</dt>
                    <dd className="text-sm text-gray-900">{item?.customer?.customer_name || 'N/A'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Email</dt>
                    <dd className="text-sm text-gray-900">{item?.customer?.email || 'N/A'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Phone</dt>
                    <dd className="text-sm text-gray-900">{item?.customer?.phone || 'N/A'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Company</dt>
                    <dd className="text-sm text-gray-900">{item?.customer?.company_name || 'N/A'}</dd>
                  </div>
                </div>
              </div>
            </div>
            {/* Line Items */}
            {item?.invoice_line_items && item?.invoice_line_items?.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Line Items</h3>
                <LineItemsTable
                  lineItems={item?.invoice_line_items?.map(lineItem => ({
                    ...lineItem,
                    item_description: lineItem?.item_description,
                    quantity: lineItem?.quantity,
                    unit_price: lineItem?.unit_price,
                    tax_rate: lineItem?.tax_rate,
                    tax_amount: lineItem?.tax_amount,
                    line_total: lineItem?.line_total,
                    line_total_with_tax: lineItem?.line_total_with_tax,
                    notes: lineItem?.notes
                  }))}
                  onChange={() => { }} // Read-only
                  itemOptions={[]}
                  taxSettings={{
                    taxType: item?.tax_type || 'tax_exclusive',
                    taxLevel: item?.tax_level || 'line_item_level'
                  }}
                  currency={item?.currency_code}
                  readOnly
                />
              </div>
            )}
            {/* Payment Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Payment Information</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Amount Paid</dt>
                    <dd className="text-sm text-gray-900">{formatCurrency(item?.amount_paid, item?.currency_code)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Balance Due</dt>
                    <dd className="text-sm text-gray-900">{formatCurrency(item?.balance_due, item?.currency_code)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Paid At</dt>
                    <dd className="text-sm text-gray-900">{item?.paid_at ? formatDate(item?.paid_at) : 'Not paid'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Currency</dt>
                    <dd className="text-sm text-gray-900">{item?.currency_code || 'USD'}</dd>
                  </div>
                </div>
              </div>
            </div>
            {/* Notes */}
            {item?.notes && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Notes</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-900">{item?.notes}</p>
                </div>
              </div>
            )}
          </div>
        );

      case 'quotes':
        return (
          <div className="space-y-6">
            {/* Quote Header */}
            <div className="border-b pb-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {item?.quote_number}
                  </h2>
                  <p className="text-gray-600">Quote Details</p>
                </div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(item?.status)}`}>
                  {item?.status?.replace('_', ' ')?.toUpperCase()}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Issue Date</dt>
                  <dd className="text-sm text-gray-900">{formatDate(item?.issue_date)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Valid Until</dt>
                  <dd className="text-sm text-gray-900">{formatDate(item?.valid_until)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Validity Period</dt>
                  <dd className="text-sm text-gray-900">{item?.validity_period?.replace('days_', '') + ' days' || 'N/A'}</dd>
                </div>
              </div>
            </div>
            {/* Customer Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Customer Information</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Customer Name</dt>
                    <dd className="text-sm text-gray-900">{item?.customer?.customer_name || 'N/A'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Email</dt>
                    <dd className="text-sm text-gray-900">{item?.customer?.email || 'N/A'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Phone</dt>
                    <dd className="text-sm text-gray-900">{item?.customer?.phone || 'N/A'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Company</dt>
                    <dd className="text-sm text-gray-900">{item?.customer?.company_name || 'N/A'}</dd>
                  </div>
                </div>
              </div>
            </div>
            {/* Line Items */}
            {item?.quote_line_items && item?.quote_line_items?.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Line Items</h3>
                <LineItemsTable
                  lineItems={item?.quote_line_items?.map(lineItem => ({
                    ...lineItem,
                    item_description: lineItem?.item_description,
                    quantity: lineItem?.quantity,
                    unit_price: lineItem?.unit_price,
                    tax_rate: lineItem?.tax_rate,
                    tax_amount: lineItem?.tax_amount,
                    line_total: lineItem?.line_total,
                    line_total_with_tax: lineItem?.line_total_with_tax,
                    notes: lineItem?.notes
                  }))}
                  onChange={() => { }} // Read-only
                  itemOptions={[]}
                  taxSettings={{
                    taxType: item?.tax_type || 'tax_exclusive',
                    taxLevel: item?.tax_level || 'line_item_level'
                  }}
                  currency={item?.currency_code}
                  readOnly
                />
              </div>
            )}
            {/* Terms and Conditions */}
            {item?.terms_and_conditions && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Terms and Conditions</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-900">{item?.terms_and_conditions}</p>
                </div>
              </div>
            )}
            {/* Notes */}
            {item?.notes && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Notes</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-900">{item?.notes}</p>
                </div>
              </div>
            )}
          </div>
        );

      case 'credit-notes':
        return (
          <div className="space-y-6">
            {/* Credit Note Header */}
            <div className="border-b pb-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">
                    {item?.credit_note_number}
                  </h2>
                  <p className="text-gray-600">Credit Note Details</p>
                </div>
                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadge(item?.status)}`}>
                  {item?.status?.replace('_', ' ')?.toUpperCase()}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Issue Date</dt>
                  <dd className="text-sm text-gray-900">{formatDate(item?.created_at)}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Credit Reason</dt>
                  <dd className="text-sm text-gray-900">{item?.credit_reason?.replace('_', ' ')}</dd>
                </div>
                <div>
                  <dt className="text-sm font-medium text-gray-500">Total Credit</dt>
                  <dd className="text-sm text-gray-900">{formatCurrency(item?.total_credit_amount, item?.currency_code)}</dd>
                </div>
              </div>
            </div>
            {/* Customer Information */}
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-3">Customer Information</h3>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Customer Name</dt>
                    <dd className="text-sm text-gray-900">{item?.customer?.customer_name || 'N/A'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Email</dt>
                    <dd className="text-sm text-gray-900">{item?.customer?.email || 'N/A'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Phone</dt>
                    <dd className="text-sm text-gray-900">{item?.customer?.phone || 'N/A'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Company</dt>
                    <dd className="text-sm text-gray-900">{item?.customer?.company_name || 'N/A'}</dd>
                  </div>
                </div>
              </div>
            </div>
            {/* Reference Invoice */}
            {item?.reference_invoice && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Reference Invoice</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Invoice Number</dt>
                      <dd className="text-sm text-gray-900">{item?.reference_invoice?.invoice_number}</dd>
                    </div>
                    <div>
                      <dt className="text-sm font-medium text-gray-500">Invoice Date</dt>
                      <dd className="text-sm text-gray-900">{formatDate(item?.reference_invoice?.issue_date)}</dd>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {/* Line Items */}
            {item?.credit_note_line_items && item?.credit_note_line_items?.length > 0 && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Line Items</h3>
                <LineItemsTable
                  lineItems={item?.credit_note_line_items?.map(lineItem => ({
                    ...lineItem,
                    item_description: lineItem?.item_description,
                    quantity: lineItem?.quantity,
                    unit_price: lineItem?.unit_price,
                    tax_rate: lineItem?.tax_rate,
                    tax_amount: lineItem?.tax_amount,
                    line_total: lineItem?.line_total,
                    line_total_with_tax: lineItem?.line_total_with_tax,
                    notes: lineItem?.notes
                  }))}
                  onChange={() => { }} // Read-only
                  itemOptions={[]}
                  taxSettings={{
                    taxType: item?.tax_type || 'tax_exclusive',
                    taxLevel: item?.tax_level || 'line_item_level'
                  }}
                  currency={item?.currency_code}
                  readOnly
                />
              </div>
            )}
            {/* Description */}
            {item?.description && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Description</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-900">{item?.description}</p>
                </div>
              </div>
            )}
            {/* Reason Description */}
            {item?.reason_description && (
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-3">Reason Description</h3>
                <div className="bg-gray-50 p-4 rounded-lg">
                  <p className="text-sm text-gray-900">{item?.reason_description}</p>
                </div>
              </div>
            )}
          </div>
        );

      case 'customers':
        return (
          <div className="space-y-6">
            {/* Basic Information */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Basic Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Customer Name</label>
                  <p className="text-sm text-gray-900">{item?.customer_name || '-'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Customer Number</label>
                  <p className="text-sm font-mono text-gray-900">{item?.customer_number || '-'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Email</label>
                  <p className="text-sm text-blue-600">{item?.email || '-'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Phone</label>
                  <p className="text-sm text-gray-900">{item?.phone || '-'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Company</label>
                  <p className="text-sm text-gray-900">{item?.company_name || '-'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Status</label>
                  <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${item?.status === 'active' ? 'bg-green-100 text-green-800' :
                      item?.status === 'inactive' ? 'bg-gray-100 text-gray-800' : 'bg-red-100 text-red-800'
                    }`}>
                    {item?.status?.charAt(0)?.toUpperCase() + item?.status?.slice(1)}
                  </span>
                </div>
              </div>
            </div>

            {/* Financial Information */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Financial Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Outstanding Amount</label>
                  <p className={`text-sm font-medium ${parseFloat(item?.total_outstanding || 0) > 0 ? 'text-orange-600' : 'text-green-600'}`}>
                    ${parseFloat(item?.total_outstanding || 0)?.toLocaleString()}
                  </p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Credit Limit</label>
                  <p className="text-sm text-gray-900">${parseFloat(item?.credit_limit || 0)?.toLocaleString()}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Payment Terms</label>
                  <p className="text-sm text-gray-900">{item?.payment_terms || 'Net 30'}</p>
                </div>
              </div>
            </div>

            {/* Addresses */}
            {item?.customer_addresses?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Addresses</h4>
                <div className="space-y-3">
                  {item?.customer_addresses?.map((address, index) => (
                    <div key={index} className="p-3 border border-gray-200 rounded-md">
                      <div className="flex justify-between items-start mb-2">
                        <span className="text-xs font-medium text-gray-500 uppercase">{address?.address_type} Address</span>
                      </div>
                      <p className="text-sm text-gray-900">{address?.street_address}</p>
                      {address?.address_line_2 && (
                        <p className="text-sm text-gray-900">{address?.address_line_2}</p>
                      )}
                      <p className="text-sm text-gray-900">
                        {address?.city}, {address?.state} {address?.postal_code}
                      </p>
                      <p className="text-sm text-gray-900">{address?.country}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Contacts */}
            {item?.customer_contacts?.length > 0 && (
              <div>
                <h4 className="text-sm font-semibold text-gray-900 mb-3">Contacts</h4>
                <div className="space-y-3">
                  {item?.customer_contacts?.map((contact, index) => (
                    <div key={index} className="p-3 border border-gray-200 rounded-md">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Name</label>
                          <p className="text-sm text-gray-900">{contact?.contact_name || '-'}</p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Type</label>
                          <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded-full">
                            {contact?.contact_type}
                          </span>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Email</label>
                          <p className="text-sm text-blue-600">{contact?.email || '-'}</p>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-500">Phone</label>
                          <p className="text-sm text-gray-900">{contact?.phone || '-'}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Statistics */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Statistics</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-3 bg-blue-50 rounded-md">
                  <p className="text-xs font-medium text-blue-600 uppercase tracking-wider">Invoices</p>
                  <p className="text-2xl font-bold text-blue-900">{item?.invoices?.[0]?.count || 0}</p>
                </div>
                <div className="p-3 bg-green-50 rounded-md">
                  <p className="text-xs font-medium text-green-600 uppercase tracking-wider">Quotes</p>
                  <p className="text-2xl font-bold text-green-900">{item?.quotes?.[0]?.count || 0}</p>
                </div>
                <div className="p-3 bg-purple-50 rounded-md">
                  <p className="text-xs font-medium text-purple-600 uppercase tracking-wider">Payments</p>
                  <p className="text-2xl font-bold text-purple-900">{item?.payments_received?.[0]?.count || 0}</p>
                </div>
              </div>
            </div>

            {/* Creation Information */}
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Creation Information</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Created By</label>
                  <p className="text-sm text-gray-900">{item?.created_by_profile?.full_name || 'System'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-1">Created Date</label>
                  <p className="text-sm text-gray-900">{new Date(item?.created_at)?.toLocaleString()}</p>
                </div>
              </div>
            </div>
          </div>
        );

      case 'payment-links':
        return (
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Payment Link Details</h4>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Link Reference</dt>
                    <dd className="text-sm text-gray-900">{item?.link_reference}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Status</dt>
                    <dd className={`text-sm font-medium ${getStatusBadge(item?.status)}`}>
                      {item?.status?.replace('_', ' ')?.toUpperCase()}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Created At</dt>
                    <dd className="text-sm text-gray-900">{formatDate(item?.created_at)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Expires At</dt>
                    <dd className="text-sm text-gray-900">{formatDate(item?.expires_at)}</dd>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'payments-received':
        return (
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Payment Details</h4>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Payment Reference</dt>
                    <dd className="text-sm text-gray-900">{item?.payment_reference}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Amount</dt>
                    <dd className="text-sm text-gray-900">{formatCurrency(item?.amount_received, item?.currency_code)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Payment Method</dt>
                    <dd className="text-sm text-gray-900">{item?.payment_method}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Status</dt>
                    <dd className={`text-sm font-medium ${getStatusBadge(item?.status)}`}>
                      {item?.status?.replace('_', ' ')?.toUpperCase()}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Payment Date</dt>
                    <dd className="text-sm text-gray-900">{formatDate(item?.payment_date)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Currency</dt>
                    <dd className="text-sm text-gray-900">{item?.currency_code || 'USD'}</dd>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'delivery-challans':
        return (
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Delivery Challan Details</h4>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Challan Number</dt>
                    <dd className="text-sm text-gray-900">{item?.challan_number}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Status</dt>
                    <dd className={`text-sm font-medium ${getStatusBadge(item?.status)}`}>
                      {item?.status?.replace('_', ' ')?.toUpperCase()}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Created At</dt>
                    <dd className="text-sm text-gray-900">{formatDate(item?.created_at)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Delivery Date</dt>
                    <dd className="text-sm text-gray-900">{formatDate(item?.delivery_date)}</dd>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'retainer-invoices':
        return (
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Retainer Details</h4>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Retainer Number</dt>
                    <dd className="text-sm text-gray-900">{item?.retainer_number}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Status</dt>
                    <dd className={`text-sm font-medium ${getStatusBadge(item?.status)}`}>
                      {item?.status?.replace('_', ' ')?.toUpperCase()}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Created At</dt>
                    <dd className="text-sm text-gray-900">{formatDate(item?.created_at)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Due Date</dt>
                    <dd className="text-sm text-gray-900">{formatDate(item?.due_date)}</dd>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 'recurring-invoices':
        return (
          <div className="space-y-6">
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-3">Recurring Invoice Details</h4>
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Recurring Reference</dt>
                    <dd className="text-sm text-gray-900">{item?.recurring_reference}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Status</dt>
                    <dd className={`text-sm font-medium ${getStatusBadge(item?.status)}`}>
                      {item?.status?.replace('_', ' ')?.toUpperCase()}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Next Due Date</dt>
                    <dd className="text-sm text-gray-900">{formatDate(item?.next_due_date)}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500">Frequency</dt>
                    <dd className="text-sm text-gray-900">{item?.frequency || 'Monthly'}</dd>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      default:
        return (
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900">{type?.slice(0, -1)} Details</h3>
            <pre className="bg-gray-50 p-4 rounded-lg text-sm overflow-auto">
              {JSON.stringify(item, null, 2)}
            </pre>
          </div>
        );
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>

        {/* Modal panel - make it larger for line items */}
        <div className={`inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle ${['invoices', 'quotes', 'credit-notes']?.includes(type)
            ? 'sm:max-w-6xl w-full' : 'sm:max-w-2xl sm:w-full'
          } sm:p-6`}>
          {/* Header */}
          <div className="flex items-center justify-between mb-6 border-b pb-4">
            <h3 className="text-lg font-medium text-gray-900">
              {type?.slice(0, -1)?.replace(/([A-Z])/g, ' $1')?.replace(/^./, str => str?.toUpperCase())} Details
            </h3>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Content */}
          <div className="max-h-[80vh] overflow-y-auto">
            {renderContent()}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 pt-6 border-t border-gray-200 mt-6">
            <Button
              variant="outline"
              onClick={onClose}
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ViewDetailsModal;
